import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  User, 
  FolderKanban, 
  Ticket, 
  FileText, 
  Bell,
  ChevronRight,
  CheckCircle2,
  Clock,
  AlertCircle
} from 'lucide-react';
import { projectAPI, ticketAPI } from '../../services/api';

const ClientDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    projects: 0,
    tickets: 0,
    documents: 0
  });
  const [recentProjects, setRecentProjects] = useState([]);
  const [recentTickets, setRecentTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [projectsRes, ticketsRes] = await Promise.all([
        projectAPI.getAll({ limit: 3 }),
        ticketAPI.getAll({ limit: 3 })
      ]);

      setStats({
        projects: projectsRes.data.pagination?.total || 0,
        tickets: ticketsRes.data.pagination?.total || 0,
        documents: 0
      });

      setRecentProjects(projectsRes.data.projects || []);
      setRecentTickets(ticketsRes.data.tickets || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const sidebarLinks = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', active: true },
    { name: 'Profile', icon: User, path: '/dashboard/profile' },
    { name: 'Projects', icon: FolderKanban, path: '/dashboard/projects' },
    { name: 'Support Tickets', icon: Ticket, path: '/dashboard/tickets' },
    { name: 'Documents', icon: FileText, path: '/dashboard/documents' },
  ];

  const statCards = [
    { name: 'Active Projects', value: stats.projects, icon: FolderKanban, color: 'bg-blue-500' },
    { name: 'Open Tickets', value: stats.tickets, icon: Ticket, color: 'bg-orange-500' },
    { name: 'Documents', value: stats.documents, icon: FileText, color: 'bg-green-500' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-slate-200 min-h-screen fixed left-0 top-0 pt-20 hidden lg:block">
          <div className="p-4">
            <div className="mb-8 px-4">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Main Menu</p>
            </div>
            <nav className="space-y-1">
              {sidebarLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`sidebar-link ${link.active ? 'active' : ''}`}
                >
                  <link.icon className="w-5 h-5" />
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-64 pt-20">
          <div className="p-6 lg:p-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-bold text-primary">Dashboard</h1>
                <p className="text-slate-600">Welcome back, {user?.name}</p>
              </div>
              <button className="relative p-2 text-slate-400 hover:text-primary transition-colors">
                <Bell className="w-6 h-6" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
            </div>

            {/* Stats */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {statCards.map((stat, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-500 text-sm">{stat.name}</p>
                      <p className="text-3xl font-bold text-primary mt-1">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center text-white`}>
                      <stat.icon className="w-6 h-6" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Recent Projects */}
              <div className="bg-white rounded-xl shadow-sm">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <h2 className="text-lg font-bold text-primary">Recent Projects</h2>
                  <Link to="/dashboard/projects" className="text-accent text-sm font-medium flex items-center gap-1">
                    View All
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
                <div className="p-6">
                  {recentProjects.length > 0 ? (
                    <div className="space-y-4">
                      {recentProjects.map((project) => (
                        <div key={project._id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center text-accent">
                            <FolderKanban className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-primary">{project.title}</h3>
                            <p className="text-sm text-slate-500">{project.projectCode}</p>
                          </div>
                          <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                            project.status === 'completed' ? 'bg-green-100 text-green-700' :
                            project.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {project.status.replace('_', ' ')}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FolderKanban className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No active projects</p>
                      <Link to="/services" className="text-accent text-sm mt-2 inline-block">
                        Explore our services
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Tickets */}
              <div className="bg-white rounded-xl shadow-sm">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <h2 className="text-lg font-bold text-primary">Support Tickets</h2>
                  <Link to="/dashboard/tickets" className="text-accent text-sm font-medium flex items-center gap-1">
                    View All
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
                <div className="p-6">
                  {recentTickets.length > 0 ? (
                    <div className="space-y-4">
                      {recentTickets.map((ticket) => (
                        <div key={ticket._id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600">
                            <Ticket className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-primary">{ticket.subject}</h3>
                            <p className="text-sm text-slate-500">{ticket.ticketNumber}</p>
                          </div>
                          <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                            ticket.status === 'resolved' ? 'bg-green-100 text-green-700' :
                            ticket.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {ticket.status.replace('_', ' ')}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CheckCircle2 className="w-12 h-12 text-teal mx-auto mb-3" />
                      <p className="text-slate-500">No open tickets</p>
                      <Link to="/dashboard/tickets" className="text-accent text-sm mt-2 inline-block">
                        Create a ticket
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-8">
              <h2 className="text-lg font-bold text-primary mb-4">Quick Actions</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { name: 'Create Support Ticket', icon: Ticket, path: '/dashboard/tickets', color: 'bg-orange-500' },
                  { name: 'View Documents', icon: FileText, path: '/dashboard/documents', color: 'bg-green-500' },
                  { name: 'Update Profile', icon: User, path: '/dashboard/profile', color: 'bg-blue-500' },
                  { name: 'Contact Support', icon: AlertCircle, path: '/contact', color: 'bg-purple-500' },
                ].map((action, index) => (
                  <Link
                    key={index}
                    to={action.path}
                    className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className={`w-10 h-10 ${action.color} rounded-lg flex items-center justify-center text-white`}>
                      <action.icon className="w-5 h-5" />
                    </div>
                    <span className="font-medium text-primary">{action.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ClientDashboard;
